import { Box, Container, Grid2, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';
import { Waypoint } from 'react-waypoint';

import ActivityCard from '@components/CollapsibleCard/ActivityCard';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import NullStateDiv from '@components/NullStateDiv';
import TimelineLoader from '@components/TimelineLoader';
import TimelineTree from '@components/TimelineTree/index';

import { useGetTerraActivities } from '@hooks/api/activity';
import { useIsMobile } from '@hooks/useIsMobile';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import type { ActivityModel } from 'generated/types';

const messages = defineMessages({
  noData: {
    id: 'activityMain.noData',
    defaultMessage: 'No Activities',
  },
  error: {
    id: 'activityMain.error',
    defaultMessage:
      'An error occurred while trying to display more activities.',
  },
  allShown: {
    id: 'activityMain.allShown',
    defaultMessage: 'All Activities Shown',
  },
});

const ActivityMain = () => {
  const { formatMessage } = useIntl();
  const isMobile = useIsMobile();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { activeProfile },
  } = useSession();
  const vitall_patient_id = activeProfile?.vitall_patient_id;

  const { data: activityData, loading, fetchMore } = useGetTerraActivities();
  const data = useMemo(() => activityData?.data ?? [], [activityData?.data]);

  const handleEnter = async () => {
    if (!vitall_patient_id || !activityData?.cursor || !activityData.hasMore) {
      return;
    }
    try {
      await fetchMore({
        variables: {
          input: {
            vitall_patient_id,
            filters: {
              cursor: activityData.cursor,
            },
          },
        },
      });
    } catch (e) {
      enqueueSnackbar({
        message: formatMessage(messages.error),
        variant: 'error',
      });
    }
  };

  const sortedActivities = data.reduce<Record<string, ActivityModel[]>>(
    (acc, curr) => {
      const year = dayjs(curr.start_time).format('YYYY');
      if (!(year in acc)) {
        acc[year] = [curr];
      } else {
        acc[year].push(curr);
      }
      return acc;
    },
    {}
  );

  const activities = Object.keys(sortedActivities)
    .sort((a, b) => Number(b) - Number(a))
    .map((year, yearIndex) => {
      return (
        <Box sx={{ display: 'flex' }} key={`activity-timeline-${yearIndex}`}>
          <TimelineTree
            header={loading ? undefined : year}
            nodes={sortedActivities[year].map((d, activityIndex) => (
              <Grid2
                key={`activity-card-${d.summary_id}`}
                sx={{
                  my: {
                    xs: 3,
                    md: 0,
                  },
                }}
              >
                <ActivityCard
                  data={d}
                  isExpanded={yearIndex === 0 && activityIndex === 0}
                  loading={loading}
                />
              </Grid2>
            ))}
          />
        </Box>
      );
    });

  return (
    <Container maxWidth="lg" sx={{ mt: 2 }}>
      {data.length > 0 && loading && activities}
      <TimelineLoader count={isMobile ? 4 : 8} loading={loading}>
        {data.length ? (
          activities
        ) : (
          <NullStateDiv>{formatMessage(messages.noData)}</NullStateDiv>
        )}
        {!activityData?.hasMore && (
          <Typography py={3} textAlign="center" variant="subtitle1">
            {formatMessage(messages.allShown)}
          </Typography>
        )}
      </TimelineLoader>

      <Waypoint onEnter={handleEnter} />
    </Container>
  );
};

export default withErrorBoundary(ActivityMain, {
  FallbackComponent: ErrorBoundaryFallback,
});
